'use client';

import { useQuery } from '@tanstack/react-query';
import { useParams } from 'next/navigation';
import { createContext, ReactNode, useContext, useMemo } from 'react';

import { orpc } from '@/lib/api';

// Analytics-specific project data shape
interface ProjectAnalyticsData {
  project_id: string;
  project_name: string;
  project_url: string;
  project_editor_url: string;
  project_role: string;
}

interface ProjectAnalyticsContextType {
  projectData: ProjectAnalyticsData | null;
  isLoading: boolean;
  error: Error | null;
}

// Create context with undefined default to detect missing provider
const ProjectAnalyticsContext = createContext<
  ProjectAnalyticsContextType | undefined
>(undefined);

interface ProjectAnalyticsProviderProps {
  children: ReactNode;
  projectId: string;
}

export function ProjectAnalyticsProvider({
  children,
  projectId,
}: ProjectAnalyticsProviderProps) {
  // Fetch project data using TanStack Query
  const {
    data: project,
    isLoading,
    error,
  } = useQuery({
    ...orpc.projects.get.queryOptions({
      input: { id: projectId },
    }),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnMount: true,
  });

  // Fetch permissions to get user's role
  const { data: permissions, isLoading: permissionsLoading } = useQuery({
    ...orpc.projects.getPermissions.queryOptions({
      input: { projectId },
    }),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // Match project query cache
  });

  // Transform to analytics format
  const projectData: ProjectAnalyticsData | null =
    project && permissions
      ? {
          project_id: project.id,
          project_name: project.name || '',
          project_url: project.subdomain
            ? `https://${project.subdomain}-dev.fly.dev`
            : '',
          project_editor_url: project.editorServiceUrl || '',
          project_role: permissions.role || 'no_role',
        }
      : null;

  // Detect stale data during project transitions
  const isTransitioning = projectData && projectData.project_id !== projectId;

  // Memoize to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      projectData: isTransitioning ? null : projectData,
      isLoading: isLoading || permissionsLoading || !!isTransitioning,
      error: error ?? null,
    }),
    [
      projectData,
      isLoading,
      permissionsLoading,
      error,
      isTransitioning,
      projectId,
    ]
  );

  return (
    <ProjectAnalyticsContext.Provider value={contextValue}>
      {children}
    </ProjectAnalyticsContext.Provider>
  );
}

// Wrapper component that reads useParams() (avoids SSR hydration issues)
export function ProjectAnalyticsProviderWrapper({
  children,
}: {
  children: ReactNode;
}) {
  const params = useParams();
  const projectId = params?.id as string;

  // Render children without provider if no projectId
  if (!projectId) {
    return <>{children}</>;
  }

  return (
    <ProjectAnalyticsProvider projectId={projectId}>
      {children}
    </ProjectAnalyticsProvider>
  );
}

/**
 * Hook to access project analytics data.
 * Must be used within ProjectAnalyticsProvider.
 */
export function useProjectAnalytics(): ProjectAnalyticsContextType {
  const context = useContext(ProjectAnalyticsContext);

  if (context === undefined) {
    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        'useProjectAnalytics must be used within ProjectAnalyticsProvider. ' +
          'Ensure your component is inside the /project/[id] route.'
      );
    }

    // Production: return safe defaults
    console.warn(
      '[ProjectAnalytics] Hook called outside provider - returning defaults'
    );
    return {
      projectData: null,
      isLoading: false,
      error: null,
    };
  }

  return context;
}
